import React, { useState, useEffect } from "react";
import { ShieldCheck, Plus, Mail, RefreshCw, Play, Loader2 } from "lucide-react";
import { getChecks, createCheck, runVerification, resendCheckEmail } from "../../api";
import { useToast } from "../../hooks/use-toast";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const stepColor = {
  verified: "bg-green-100 text-green-700", clear: "bg-green-100 text-green-700",
  review: "bg-amber-100 text-amber-700", hit: "bg-red-100 text-red-600",
  pending: "bg-gray-100 text-gray-500", "n/a": "bg-gray-50 text-gray-400",
};
const statusColor = {
  "verification-sent": "bg-blue-100 text-blue-700", verified: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-600", pending: "bg-gray-100 text-gray-500",
};
const empty = { name: "", email: "", phone: "", type: "right-to-rent", role: "tenant", propertyTitle: "", nationality: "", dob: "" };

const AdminCompliance = () => {
  const { toast } = useToast();
  const [checks, setChecks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(null);

  const refresh = () => getChecks().then(setChecks).catch(() => setChecks([]));
  useEffect(() => { refresh(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try { await createCheck(form); toast({ title: "Check created", description: "Verification email sent (mocked)." }); setOpen(false); setForm(empty); refresh(); }
    catch (err) { toast({ title: "Failed", description: err?.response?.data?.detail || "" }); }
  };
  const verify = async (id) => {
    setBusy(id);
    try { const r = await runVerification(id); toast({ title: `Verification ${r.status}` }); refresh(); }
    catch { toast({ title: "Verification failed" }); } finally { setBusy(null); }
  };
  const resend = async (id) => {
    try { await resendCheckEmail(id); toast({ title: "Reminder email sent (mocked)" }); refresh(); }
    catch { toast({ title: "Couldn't resend" }); }
  };

  const inputCls = "w-full h-[44px] px-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]";

  return (
    <div className="bg-white rounded-2xl border border-[#f0ece5] p-6" data-testid="admin-compliance">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display text-[20px] font-500 text-[#2b2b2b] flex items-center gap-2"><ShieldCheck size={19} className="text-[#b8935f]" /> AML & Right-to-Rent ({checks.length})</h3>
        <button onClick={() => setOpen(true)} data-testid="new-check-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#b8935f] text-white text-[13px] font-500 hover:bg-[#a67f4d] transition-colors"><Plus size={15} /> New Check</button>
      </div>
      <p className="text-[12px] text-[#a0a0a0] mb-4">Automated identity, sanctions & PEP screening. Verification emails are MOCKED until a Resend key is added.</p>

      {checks.length === 0 ? <p className="p-8 text-center text-[#8a8a8a]">No compliance checks yet.</p> : (
        <div className="space-y-3">
          {checks.map((c) => (
            <div key={c.id} className="p-4 rounded-xl bg-[#faf7f2] border border-[#f0ece5]" data-testid="compliance-row">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <p className="text-[15px] font-500 text-[#2b2b2b]">{c.name} <span className="text-[11px] uppercase tracking-wide text-[#b8935f] ml-2">{c.type === "aml" ? "AML" : "Right to Rent"}</span></p>
                  <p className="text-[13px] text-[#8a8a8a]">{c.email} · {c.role}{c.propertyTitle ? ` · ${c.propertyTitle}` : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[11px] px-2.5 py-1 rounded-full capitalize ${statusColor[c.status] || "bg-gray-100"}`}>{c.status.replace("-", " ")}</span>
                  <button onClick={() => verify(c.id)} disabled={busy === c.id} title="Run verification" data-testid="run-verify-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#e2d8c8] text-[12px] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors disabled:opacity-60">{busy === c.id ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} Verify</button>
                  <button onClick={() => resend(c.id)} title="Resend email" className="w-9 h-9 rounded-lg border border-[#e2d8c8] flex items-center justify-center text-[#6a6a6a] hover:text-[#b8935f] hover:border-[#b8935f] transition-colors"><RefreshCw size={14} /></button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {Object.entries(c.steps || {}).map(([k, v]) => (
                  <span key={k} className={`text-[11px] px-2 py-1 rounded-md ${stepColor[v] || "bg-gray-100"}`}>{k}: {v}</span>
                ))}
              </div>
              {c.documents?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="agent-doc-list">
                  {c.documents.map((d, i) => (
                    <a key={i} href={d.url} target="_blank" rel="noreferrer" className="text-[11px] px-2.5 py-1 rounded-md bg-[#faf7f2] border border-[#e2d8c8] text-[#6a6a6a] hover:text-[#b8935f] hover:border-[#b8935f] transition-colors">{d.label}: {d.filename}</a>
                  ))}
                </div>
              )}
              {c.emailLog?.length > 0 && <p className="text-[11px] text-[#a0a0a0] mt-2 flex items-center gap-1"><Mail size={11} /> {c.emailLog.length} email{c.emailLog.length > 1 ? "s" : ""} sent (mocked)</p>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogTitle className="sr-only">New Compliance Check</DialogTitle>
          <h2 className="font-display text-[24px] font-300 text-[#2b2b2b] mb-4">New Compliance Check</h2>
          <form onSubmit={submit} className="space-y-3">
            <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="check-name-input" />
            <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="check-email-input" />
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="h-[44px] border-[#e2d8c8]" data-testid="check-type-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="right-to-rent">Right to Rent</SelectItem><SelectItem value="aml">AML / Identity</SelectItem></SelectContent>
              </Select>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="h-[44px] border-[#e2d8c8]"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="tenant">Tenant</SelectItem><SelectItem value="buyer">Buyer</SelectItem><SelectItem value="vendor">Vendor</SelectItem><SelectItem value="landlord">Landlord</SelectItem></SelectContent>
              </Select>
            </div>
            <input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
            <input placeholder="Property / tenancy (optional)" value={form.propertyTitle} onChange={(e) => setForm({ ...form, propertyTitle: e.target.value })} className={inputCls} />
            <button type="submit" data-testid="submit-check-btn" className="w-full h-[48px] rounded-lg bg-[#b8935f] text-white text-[15px] font-500 hover:bg-[#a67f4d] transition-colors">Create & Send Verification</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCompliance;
